import React, { useState, useEffect } from 'react';
import { drugService } from '../../services/drugService';
import { Drug, DrugPrice, UserDrugs } from '../../types';

interface DrugsProps {
  user: any;
  onUpdate: () => void;
}

const Drugs: React.FC<DrugsProps> = ({ user, onUpdate }) => {
  const [drugs, setDrugs] = useState<Drug[]>([]);
  const [drugPrices, setDrugPrices] = useState<DrugPrice[]>([]);
  const [userDrugs, setUserDrugs] = useState<UserDrugs[]>([]);
  const [drugCapacity, setDrugCapacity] = useState(100);
  const [currentQuantity, setCurrentQuantity] = useState(0);
  const [remainingCapacity, setRemainingCapacity] = useState(100);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [buyQuantities, setBuyQuantities] = useState<{ [key: number]: number }>({});

  useEffect(() => {
    loadDrugData();
  }, []);

  const loadDrugData = async () => {
    try {
      setLoading(true);
      const [drugsData, pricesData, userDrugsData] = await Promise.all([
        drugService.getDrugs(),
        drugService.getDrugPrices(),
        drugService.getUserDrugs()
      ]);

      if (drugsData.success) setDrugs(drugsData.drugs);
      if (pricesData.success) setDrugPrices(pricesData.prices);
      if (userDrugsData.success) {
        setUserDrugs(userDrugsData.userDrugs);
        setDrugCapacity(userDrugsData.capacity);
        setCurrentQuantity(userDrugsData.currentQuantity);
        setRemainingCapacity(userDrugsData.remainingCapacity);
      }
    } catch (error) {
      console.error('Error loading drug data:', error);
      // Fallback data
      setDrugs([
        { id: 1, name: 'Marijuana', basePrice: 10, description: 'Common street drug, low risk', riskLevel: 'Low', weight: 1 },
        { id: 2, name: 'Cocaine', basePrice: 80, description: 'High-value stimulant, medium risk', riskLevel: 'Medium', weight: 1 },
        { id: 3, name: 'Heroin', basePrice: 150, description: 'Highly addictive opiate, high risk', riskLevel: 'High', weight: 1 },
        { id: 4, name: 'Methamphetamine', basePrice: 120, description: 'Synthetic stimulant, high risk', riskLevel: 'High', weight: 1 },
        { id: 5, name: 'Ecstasy', basePrice: 25, description: 'Party drug, medium risk', riskLevel: 'Medium', weight: 1 }
      ]);
      setUserDrugs([]);
      setDrugCapacity(100);
      setCurrentQuantity(0);
      setRemainingCapacity(100);
    } finally {
      setLoading(false);
    }
  };

  const handleBuyDrug = async (drugId: number) => {
    const quantity = buyQuantities[drugId] || 0;
    if (quantity <= 0) {
      setMessage('Please enter a quantity to buy');
      return;
    }

    if (quantity > remainingCapacity) {
      setMessage(`You can only carry ${remainingCapacity}g more. Current capacity: ${currentQuantity}g/${drugCapacity}g`);
      return;
    }

    try {
      const result = await drugService.buyDrug({ drugId, quantity });
      if (result.success) {
        setMessage(`Successfully bought ${quantity}g of ${result.drugName} for $${result.moneySpent}`);
        setBuyQuantities({ ...buyQuantities, [drugId]: 0 });
        loadDrugData();
        onUpdate();
      } else {
        setMessage(result.message);
      }
    } catch (error) {
      setMessage('Error buying drug. Please try again.');
    }
  };

  const handleSellDrug = async (drugId: number, quantity: number) => {
    try {
      const result = await drugService.sellDrug({ drugId, quantity });
      if (result.success) {
        setMessage(`Successfully sold ${quantity}g of ${result.drugName} for $${result.moneyEarned}`);
        loadDrugData();
        onUpdate();
      } else {
        setMessage(result.message);
      }
    } catch (error) {
      setMessage('Error selling drug. Please try again.');
    }
  };

  const getDrugPrice = (drugId: number) => {
    const price = drugPrices.find(p => p.drugId === drugId && p.locationId === user.location);
    return price ? price.price : 0;
  };

  const getUserDrugQuantity = (drugId: number) => {
    const userDrug = userDrugs.find(d => d.drugId === drugId);
    return userDrug ? userDrug.quantity : 0;
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'Low': return '#4CAF50';
      case 'Medium': return '#FF9800';
      case 'High': return '#F44336';
      default: return '#666';
    }
  };

  const formatPrice = (price: number) => {
    return `$${price.toLocaleString()}`;
  };

  const formatQuantity = (quantity: number) => {
    return `${quantity}g`;
  };

  const canAfford = (drugId: number, quantity: number) => {
    const price = getDrugPrice(drugId);
    return user.money >= price * quantity;
  };

  const canSell = (drugId: number, quantity: number) => {
    const userQuantity = getUserDrugQuantity(drugId);
    return userQuantity >= quantity;
  };

  if (loading) {
    return <div className="drugs-page">Loading drug market...</div>;
  }

  return (
    <div className="drugs-page">
      <div className="status-section">
        <h3>Current Status</h3>
        <div className="status-info">
          <p><strong>Location:</strong> {user.locationName}</p>
          <p><strong>Money:</strong> {formatPrice(user.money)}</p>
          <p><strong>Drug Capacity:</strong> {currentQuantity}g / {drugCapacity}g</p>
          <p><strong>Remaining Capacity:</strong> {remainingCapacity}g</p>
          <div className="capacity-bar" style={{
            width: '100%',
            height: '8px',
            backgroundColor: 'var(--dark-bg)',
            borderRadius: '4px',
            marginTop: '5px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${(currentQuantity / drugCapacity) * 100}%`,
              height: '100%',
              backgroundColor: currentQuantity / drugCapacity > 0.8 ? 'var(--error-color)' : 'var(--accent-color)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      </div>

      {message && (
        <div className="message" style={{
          padding: '10px',
          margin: '10px 0',
          borderRadius: '4px',
          backgroundColor: message.includes('Successfully') ? 'var(--success-color)' : 'var(--error-color)',
          color: 'white',
          textAlign: 'center'
        }}>
          {message}
        </div>
      )}

      <div className="market-section">
        <h3>Drug Market</h3>
        <div className="drugs-table">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--dark-bg)', color: 'white' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--accent-color)' }}>Drug</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--accent-color)' }}>Risk</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--accent-color)' }}>Price</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--accent-color)' }}>Your Stock</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--accent-color)' }}>Buy Quantity</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--accent-color)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {drugs.map((drug) => {
                const price = getDrugPrice(drug.id);
                const userQuantity = getUserDrugQuantity(drug.id);
                const buyQuantity = buyQuantities[drug.id] || 0;
                const canBuy = canAfford(drug.id, buyQuantity) && buyQuantity <= remainingCapacity;

                return (
                  <tr key={drug.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '12px', textAlign: 'left' }}>
                      <div>
                        <strong>{drug.name}</strong>
                        <div style={{ fontSize: '0.8em', color: '#666' }}>{drug.description}</div>
                      </div>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <span style={{ 
                        color: getRiskColor(drug.riskLevel),
                        fontWeight: 'bold'
                      }}>
                        {drug.riskLevel}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>
                      {formatPrice(price)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {formatQuantity(userQuantity)}
                      {userQuantity > 0 && (
                        <button
                          onClick={() => handleSellDrug(drug.id, Math.min(userQuantity, 10))}
                          disabled={!canSell(drug.id, 1)}
                          style={{
                            marginLeft: '8px',
                            padding: '4px 8px',
                            fontSize: '0.8em',
                            backgroundColor: 'var(--accent-color)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: canSell(drug.id, 1) ? 'pointer' : 'not-allowed',
                            opacity: canSell(drug.id, 1) ? 1 : 0.5
                          }}
                        >
                          Sell 10g
                        </button>
                      )}
                    </td>
                                         <td style={{ padding: '12px', textAlign: 'center' }}>
                       <input
                         type="number"
                         min="0"
                         max={remainingCapacity}
                         value={buyQuantity || ''}
                         onChange={(e) => {
                           const value = e.target.value;
                           const numValue = parseInt(value) || 0;
                           setBuyQuantities({
                             ...buyQuantities,
                             [drug.id]: numValue
                           });
                         }}
                         onFocus={(e) => {
                           if (e.target.value === '0') {
                             e.target.value = '';
                           }
                         }}
                         style={{
                           width: '80px',
                           padding: '6px',
                           border: '1px solid var(--border-color)',
                           borderRadius: '4px',
                           textAlign: 'center'
                         }}
                         placeholder="0"
                       />
                     </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <button
                        onClick={() => handleBuyDrug(drug.id)}
                        disabled={!canBuy || buyQuantity <= 0}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: canBuy && buyQuantity > 0 ? 'var(--accent-color)' : '#666',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: canBuy && buyQuantity > 0 ? 'pointer' : 'not-allowed',
                          opacity: canBuy && buyQuantity > 0 ? 1 : 0.5
                        }}
                      >
                        Buy
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="info-section">
        <h3>Market Information</h3>
        <div className="info-grid">
          <div className="info-card">
            <h4>Capacity System</h4>
            <p>Your drug capacity is based on your rank and applies to ALL drugs combined, not per drug type.</p>
            <ul>
              <li>Rank 1: 100g total capacity</li>
              <li>Rank 2: 150g total capacity</li>
              <li>Rank 3: 225g total capacity</li>
              <li>And so on...</li>
            </ul>
          </div>
          <div className="info-card">
            <h4>Travel Risk</h4>
            <p>Carrying drugs increases your risk of being pulled over while traveling:</p>
            <ul>
              <li>Low Risk Drugs: 5% chance</li>
              <li>Medium Risk Drugs: 15% chance</li>
              <li>High Risk Drugs: 30% chance</li>
            </ul>
          </div>
          <div className="info-card">
            <h4>Price Fluctuations</h4>
            <p>Drug prices change every hour and vary by location. Higher prices in a location mean:</p>
            <ul>
              <li>Higher travel costs to that location</li>
              <li>Longer travel times</li>
              <li>Better profit potential</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Drugs;
